import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Harcama Kategorisi ekleme/düzenleme modal'ı — Ayarlar > Harcama
 * Kategorileri sayfasıyla ve Yeni Harcama > Kategori dropdown'undaki
 * "+ Yeni kategori ekle" akışıyla birebir aynı UI/UX'i sunar.
 *
 * Props:
 *  - open (bool)                  : Dialog görünürlüğü
 *  - onOpenChange(open)           : Dialog kapama callback'i
 *  - editingCategory              : { id, name, subcategories: [{id?, name}] } | null
 *  - initialName                  : (opsiyonel) yeni kategori için ön dolgu adı
 *  - onSaved(savedCategory)       : Başarılı POST/PUT sonrası tetiklenir; parent listeyi yeniler
 */
const ExpenseCategoryFormModal = ({
  open,
  onOpenChange,
  editingCategory = null,
  initialName = '',
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [newSubcategory, setNewSubcategory] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal her açıldığında form'u yeniden hydrate et. `editingCategory`
  // değişikliği veya initialName yeni harcama akışında da doğru çalışır.
  useEffect(() => {
    if (!open) return;
    if (editingCategory) {
      setName(editingCategory.name || '');
      setSubcategories(
        (editingCategory.subcategories || []).map((s) => (typeof s === 'string' ? s : s.name))
      );
    } else {
      setName(initialName || '');
      setSubcategories([]);
    }
    setNewSubcategory('');
  }, [open, editingCategory, initialName]);

  const handleAddSubcategory = () => {
    const val = newSubcategory.trim();
    if (!val) {
      toast.error('Alt kategori adı gerekli');
      return;
    }
    if (subcategories.includes(val)) {
      toast.error('Bu alt kategori zaten eklenmiş');
      return;
    }
    setSubcategories([...subcategories, val]);
    setNewSubcategory('');
  };

  const handleRemoveSubcategory = (index) => {
    setSubcategories(subcategories.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Kategori adı gerekli');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), subcategories };
      let saved;
      if (editingCategory?.id) {
        const r = await axios.put(`${API}/harcama-kategorileri/${editingCategory.id}`, payload);
        saved = r.data;
        toast.success('Kategori güncellendi');
      } else {
        const r = await axios.post(`${API}/harcama-kategorileri`, payload);
        saved = r.data;
        toast.success('Kategori oluşturuldu');
      }
      onOpenChange(false);
      // Parent'a saved (backend response) + kullanıcının girdiği adı geç.
      if (onSaved) onSaved({ ...saved, name: name.trim(), subcategories });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kategori kaydedilirken hata oluştu';
      toast.error(typeof msg === 'string' ? msg : 'Kategori kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="expense-category-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {editingCategory ? 'Kategori Güncelle' : 'Yeni Harcama Kategorisi'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="ecfm-name">Kategori Adı *</Label>
            <Input
              id="ecfm-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Yemek, Ulaşım, Konaklama"
              className="mt-1"
              autoFocus
              data-testid="expense-category-form-name"
            />
          </div>

          <div>
            <Label>Alt Kategoriler</Label>
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <Input
                  value={newSubcategory}
                  onChange={(e) => setNewSubcategory(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSubcategory();
                    }
                  }}
                  placeholder="Alt kategori adı girin ve Enter'a basın"
                  className="flex-1"
                  data-testid="expense-category-form-sub-input"
                />
                <Button
                  type="button"
                  onClick={handleAddSubcategory}
                  variant="outline"
                  className="text-teal-600 hover:text-teal-700 hover:border-teal-600"
                  data-testid="expense-category-form-sub-add"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Ekle
                </Button>
              </div>

              {subcategories.length > 0 && (
                <div className="border rounded-md p-3 bg-gray-50 space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    Eklenmiş Alt Kategoriler ({subcategories.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {subcategories.map((sub, index) => (
                      <Badge
                        key={`${sub}-${index}`}
                        variant="secondary"
                        className="px-3 py-1 bg-teal-100 text-teal-800 hover:bg-teal-200 flex items-center gap-2"
                      >
                        {sub}
                        <button
                          type="button"
                          onClick={() => handleRemoveSubcategory(index)}
                          className="hover:text-red-600"
                          aria-label={`${sub} alt kategorisini sil`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="expense-category-form-save"
            >
              {saving ? 'Kaydediliyor...' : editingCategory ? 'Güncelle' : 'Oluştur'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseCategoryFormModal;
